/**
 * WantListPanel — the agentic-sourcing WANT-LIST surface shell
 * (docs/ASKA-PLUS-AGENTIC-SOURCING.md §2, §11 step 10). The Aska+ member benefit:
 * build a want-list, an AI broker RFQs the relevant suppliers and brings back a
 * ranked shortlist of real quotes. Rendered by both shells (like SourcingPanel).
 *
 * ⚠️ SCAFFOLD (§10). Branches on `rfqLive` exactly as SourcingPanel branches on
 * `live`: OFF (the default, and what prod serves until SOURCING_RFQ_LIVE) ⇒ the
 * pitch + "coming soon", NEVER a working CTA (the /sourcing/want-* routes answer
 * {rfq_not_configured}). The live arm is a minimal shell — the want-list book +
 * add-a-want form + ranked-quote cards are phase bodies; this scaffold establishes
 * the surface so the route exists and renders. The buyer NEVER sees a supplier
 * name (§6) — the quote cards project merchant-blind.
 */
import { useEffect, useState } from 'react'
import { SOURCING_PLAN, canUseAgenticSourcing } from '../../lib/sourcing'

/** Re-read the plan on the boot hydration broadcast (aska:sourcing-changed) so the
 *  pitch flips to the live shell the moment the flag arms, without a reload. */
function useRfqLive(): boolean {
  const [live, setLive] = useState<boolean>(canUseAgenticSourcing())
  useEffect(() => {
    const onChange = () => setLive(canUseAgenticSourcing())
    window.addEventListener('aska:sourcing-changed', onChange)
    return () => window.removeEventListener('aska:sourcing-changed', onChange)
  }, [])
  return live
}

export function WantListPanel() {
  const rfqLive = useRfqLive()

  // ── Inert: the pitch, no working CTA (prod's current + only reachable state) ──
  if (!rfqLive) {
    return (
      <div className="rounded-card border border-border bg-paper p-6 space-y-4">
        <div className="space-y-2">
          <h2 className="font-serif text-xl font-bold text-ink">Let your agent do the sourcing.</h2>
          <p className="text-sm text-soft leading-relaxed">
            Build a list of the stones you’re after — a specific stock ID, or a spec with a ceiling price.
            Your Aska+ sourcing agent reaches out to the right suppliers, collects real quotes, and brings you
            a <span className="font-semibold text-ink">ranked shortlist</span> — you pick the one you want.
          </p>
          <p className="text-xs text-soft leading-relaxed">
            You never see or manage the suppliers — the agent handles the back-and-forth. Accepting a quote
            flows into the same protected checkout as a sourced order.
          </p>
        </div>
        <span className="inline-flex items-center rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-soft">Coming soon · Aska+</span>
      </div>
    )
  }

  // ── Live shell (SCAFFOLD) — the book + form + ranked cards are phase bodies ──
  return (
    <div className="space-y-6">
      <div className="rounded-card border border-gold/30 bg-gold/[0.04] p-4">
        <p className="text-sm text-ink">
          <span className="font-semibold">Your sourcing agent is on.</span>{' '}
          Add wants and submit — the agent contacts up to {SOURCING_PLAN.rfqMaxSuppliers} suppliers per want and
          brings back a ranked shortlist. You’re only ever charged a quote you confirm.
        </p>
      </div>

      {/* TODO(agentic-sourcing:phase2): the want-list book + "add a want" form
          (reuse SourcingForm for a spec want; the stone modal's "Add to sourcing
          list" for a specific want) + the ranked, merchant-blind quote cards
          (BuyerWantView.quotes). This scaffold renders the surface + the empty
          state so the route exists and the flag flip has something to show. */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-soft">Your want-lists</h2>
        <p className="text-sm text-soft">
          No want-lists yet. Add stones from browse or a spec, then submit to start sourcing.
        </p>
      </div>
    </div>
  )
}
